/**
 * Detects boundary crossings between "guide is live" and "guide is not live"
 * (#7 — bidirectional completed_at).
 *
 * Pure function so the provider can call it on every render without side
 * effects. Caller is responsible for actually firing setMerchantSetupGuideCompletion
 * when the result is non-null.
 *
 * Returns:
 *   - null when no transition occurred (no RPC needed)
 *   - { transitionedTo: 'complete' } when crossing into live
 *   - { transitionedTo: 'incomplete' } when regressing out of live
 */
export interface CompletionTransition {
  transitionedTo: "complete" | "incomplete";
}

export function detectCompletionTransition(args: {
  /** Current snapshot: are all required-for-live tasks passing? */
  isCurrentlyLive: boolean;
  /** Persisted state: does the row already record completed_at? */
  hasPersistedCompletion: boolean;
  /**
   * True while an async task status source exists but hasn't resolved —
   * the snapshot is unknown, not incomplete, so no transition can be judged.
   */
  asyncStatusesUnresolved?: boolean;
}): CompletionTransition | null {
  if (args.asyncStatusesUnresolved) {
    return null;
  }
  if (args.isCurrentlyLive && !args.hasPersistedCompletion) {
    return { transitionedTo: "complete" };
  }
  if (!args.isCurrentlyLive && args.hasPersistedCompletion) {
    return { transitionedTo: "incomplete" };
  }
  return null;
}
